// Chat Widget Utilities

import { ERROR_MESSAGES, ERROR_TYPES, RETRY_CONFIG } from './chat-constants';

/**
 * Classify error type from error object or HTTP response
 * @param {Error|AxiosError} error
 * @returns {string} error type
 */
export function classifyError(error) {
  if (!error) return ERROR_TYPES.UNKNOWN;

  const status = error.response?.status;
  const message = error.message?.toLowerCase() || '';

  if (status === 429) return ERROR_TYPES.RATE_LIMIT;
  if (status === 400) return ERROR_TYPES.VALIDATION;
  if (status >= 500) return ERROR_TYPES.SERVER;
  if (message.includes('timeout') || message.includes('econnaborted')) {
    return ERROR_TYPES.TIMEOUT;
  }
  if (message.includes('network') || message.includes('enotfound')) {
    return ERROR_TYPES.NETWORK;
  }

  return ERROR_TYPES.UNKNOWN;
}

/**
 * Get user-friendly error message from error type
 * @param {string} errorType
 * @returns {string} message
 */
export function getErrorMessage(errorType) {
  return ERROR_MESSAGES[errorType.toUpperCase()] || ERROR_MESSAGES.UNKNOWN;
}

/**
 * Retry a function with exponential backoff
 * @param {Function} fn - Async function to retry
 * @param {Object} config - Retry configuration
 * @returns {Promise}
 */
export async function retryWithBackoff(fn, config = RETRY_CONFIG) {
  let lastError;
  let delay = config.initialDelayMs;

  for (let attempt = 0; attempt < config.maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      const errorType = classifyError(error);

      // Don't retry validation or rate limit errors on last attempt
      if ((errorType === ERROR_TYPES.VALIDATION || errorType === ERROR_TYPES.RATE_LIMIT) && attempt === config.maxAttempts - 1) {
        throw error;
      }

      // Wait before retrying (except on last attempt)
      if (attempt < config.maxAttempts - 1) {
        await new Promise(resolve => setTimeout(resolve, Math.min(delay, config.maxDelayMs)));
        delay *= config.backoffMultiplier;
      }
    }
  }

  throw lastError;
}

/**
 * Get source style based on source name
 * @param {Object} source - Source object with name property
 * @returns {Object} { bg, color }
 */
export function getSourceStyle(source) {
  const SOURCE_COLORS = {
    'ATO.gov.au': { bg: '#fff3e0', color: '#e65100' },
    'APRA.gov.au': { bg: '#e8f5e9', color: '#1b5e20' },
    'RBA.gov.au': { bg: '#e3f2fd', color: '#0d47a1' },
    'ASIC.gov.au': { bg: '#f3e5f5', color: '#4a148c' },
    'PropAtlas Guide': { bg: '#e8f5e9', color: '#1f3a2e' },
  };

  for (const [key, style] of Object.entries(SOURCE_COLORS)) {
    if (source.name.includes(key.split('.')[0]) || source.name.includes('PropAtlas')) {
      return style;
    }
  }

  return { bg: 'var(--paper-2)', color: 'var(--ink-3)' };
}

/**
 * Format date for display in chat history
 * @param {string|Date} dateString
 * @returns {string}
 */
export function formatChatDate(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

/**
 * Truncate text with ellipsis
 * @param {string} text
 * @param {number} maxLength
 * @returns {string}
 */
export function truncate(text, maxLength = 50) {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '…';
}
